import React from "react";
import { SquareButton, SquareButtonProps, MuseLogo } from "@themuse/design-system";

import { useAppState } from "hooks/AppContext";
import { slugify } from "utils/strings";
import { getAllowedLocationFromDMA, translateDMAString } from "utils/location";
import styles from "./jobsSidebar.module.scss";
import { SvgIconLocation2Shape } from "./SvgIconLocation2Shape";

interface ButtonLinkProps extends SquareButtonProps {
    clickEvent?: string;
}

export interface JobsSidebarProps {
    location?: string;
    keywords?: string[];
    searchUrl?: string;
    linkProps?: ButtonLinkProps;
}

export interface JobsIconProps {
    location?: string;
}

export interface IconContainerProps {
    location?: string;
}

const JobsHeader: React.FC<JobsSidebarProps> = ({ location }) => {
    const suffix = location ? `in ${location}` : "on The Muse";
    const headerText = `Hot Jobs ${suffix}`;
    return (
        <h2 className={styles.header}>
            <span className={styles.text}>{headerText}</span>
        </h2>
    );
};

const ButtonLink: React.FC<ButtonLinkProps> = ({
    className,
    href,
    color,
    clickEvent,
    children
}) => (
    <div className={`${styles.buttonLink} ${className || ""}`}>
        <SquareButton target="_blank" href={href} color={color} data-oskar-click={clickEvent}>
            {children}
        </SquareButton>
    </div>
);

const ExploreLink: React.FC<JobsSidebarProps & ButtonLinkProps> = ({
    searchUrl,
    location,
    ...rest
}) => {
    let url = searchUrl;
    const buttonClickTrackingProps = {
        "data-tracking-event-type": "click",
        "data-tracking-props": JSON.stringify({
            sp_click_component: "button",
            sp_click_button_type: "job_search",
            sp_click_insert_type: "hot_jobs",
            sp_click_button_text: "explore all jobs",
            sp_click_position: "right_rail",
            sp_click_target_page_position: "search"
        })
    };

    if (location) {
        url += `/location/${slugify(location)}/radius/100mi`;
    }
    return (
        <ButtonLink href={url} className={styles.explore} color="secondary-100" {...rest}>
            <span className={styles.fullText} {...buttonClickTrackingProps}>
                Explore All Jobs
            </span>
        </ButtonLink>
    );
};

const IconContainer: React.FC<IconContainerProps> = () => (
    <div className={styles.iconContainer}>
        <div className={styles.icon}>
            <div className={styles.pin}>
                <SvgIconLocation2Shape />
            </div>
        </div>
    </div>
);

const HotJobs: React.FC<JobsSidebarProps & ButtonLinkProps> = ({
    searchUrl,
    keywords,
    location,
    ...rest
}) => {
    const defaultKeywords: string[] = [
        "sales",
        "marketing",
        "product",
        "software engineering",
    ];
    const searchPath = searchUrl;
    const linkKeywords = keywords && keywords.length ? keywords : defaultKeywords;
    const hotJobsLinks = linkKeywords.map((keyword) => {
        let buttonUrl = `${searchPath}/keyword/${keyword}`;
        const buttonClickTrackingProps = {
            "data-tracking-event-type": "click",
            "data-tracking-props": JSON.stringify({
                sp_click_component: "button",
                sp_click_button_type: "job_search",
                sp_click_insert_type: "hot_jobs",
                sp_click_button_text: `${keyword} jobs`,
                sp_click_position: "right_rail",
                sp_click_target_page_position: "search"
            })
        };

        if (location) {
            buttonUrl += `/location/${slugify(location)}/radius/100mi`;
        }
        return (
            <ButtonLink key={`${keyword}`} color="accent3" {...rest} href={buttonUrl}>
                <span className="full-text" {...buttonClickTrackingProps}>
                    {keyword}
                    {" "}
                    Jobs
                </span>
            </ButtonLink>
        );
    });

    return (
        <div className={styles.hotJobs}>
            {hotJobsLinks}
        </div>
    );
};

/**
 * Renders the Hot Jobs Sidebar
 */
export const JobsSidebar: React.FC<JobsSidebarProps> = ({
    searchUrl,
    keywords,
}) => {
    const { user } = useAppState();
    const { location: { dma = "" } } = user;
    const searchPath = searchUrl || "/hiring";
    const trackingLocation = getAllowedLocationFromDMA(translateDMAString(dma));
    const moduleImpressionTrackingProps = {
        "data-tracking-event-type": "impression",
        "data-tracking-props": JSON.stringify({
            sp_impression_component: "insert",
            sp_impression_insert_type: "hot_jobs",
            sp_impression_position: "right_rail",
        })
    };

    return (
        <div className={styles.jobsSidebarWrapper}>
            <div className={styles.jobsSidebar} {...moduleImpressionTrackingProps}>
                <div className={styles.container}>
                    <JobsHeader location={trackingLocation} />
                    <HotJobs location={trackingLocation} keywords={keywords} searchUrl={searchPath} />
                    <ExploreLink location={trackingLocation} searchUrl={searchPath} />

                    {trackingLocation && (
                        <div className={styles.searchLocation}>
                            Not your city?
                            <a href={searchPath}>Search By Location</a>
                        </div>
                    )}
                    <IconContainer location={trackingLocation} />
                </div>
                <div className={styles.logo}>
                    <MuseLogo />
                </div>
            </div>
        </div>
    );
};
